/**
 * Rubric cells service — ADR 116 layered alert subscription rubric.
 *
 * Backed by the v2 endpoints:
 *   GET/POST   /api/v2/rubric/{district,school}/{id}/cells/
 *   GET/PATCH/DELETE   /api/v2/rubric/{district,school}/{id}/cells/{cellId}/
 *
 * The rubric tables are append-only — every write produces a new row and
 * closes the prior active one via `effective_until`. Callers should treat
 * the returned cell `id` as the "current" identity for the cell key; the
 * id rotates on every edit, so derived state (selection, scroll position,
 * etc.) must key off `(role, urgency, mode, alert_type)` rather than `id`.
 *
 * These paths are served by hand-rolled APIView classes rather than a
 * ModelViewSet; calls go through `client.ky` (raw ky passthrough) and the
 * lib's auth/refresh/GPC/vendor-org middleware still applies. Mirrors the
 * pattern in `roster.service.ts`.
 */
import { client } from '../lib/http-client';

// Domain values per the backend constants (apps/schools/constants.py).
//
// `follow` is intentionally omitted from `RubricUrgency`: it
// is incident-only at the AlertType level (see backend's
// `ALERT_URGENCY_TYPES` and ADR-068), and the rubric `(urgency, mode)`
// matrix only covers alert-eligible urgencies. Type-override cells
// (alert_type=<…> with urgency=null/mode=null) still cover any AlertType
// regardless of its urgency, so follow-urgency AlertTypes can still
// have rubric overrides — just not via the default-cell shape.
export type RubricUrgency = 'emergency' | 'attention';
export type RubricMode = 'alert' | 'report';

/**
 * A single rubric cell row as returned by the API.
 *
 * Cell key is one of two shapes (mutually exclusive — enforced by the
 * server's CHECK constraint):
 *   - **Default cell**: `urgency` + `mode` set, `alert_type` null.
 *     Applies to every AlertType matching `urgency` × `mode` under the
 *     scope.
 *   - **Type-override cell**: `alert_type` set, `urgency` + `mode` null.
 *     Applies to that specific AlertType only.
 *
 * Cell value is two nullable ints carrying the minimum escalation level
 * at which the role is subscribed (NULL = not subscribed, 0 = baseline,
 * 1+ = subscribed only at that escalation level or above).
 */
export interface RubricCell {
  id: number;
  role: number;
  urgency: RubricUrgency | null;
  mode: RubricMode | null;
  alert_type: number | null;
  alert_min_level: number | null;
  drill_min_level: number | null;
  effective_from: string;
  effective_until: string | null;
  created_by: number | null;
  created_at: string;
  // The scoping FK — only one of these is set per row depending on tier.
  district?: number;
  school?: number;
}

/**
 * Body shape for POST + PATCH. The view layer ignores any cell-key
 * fields on PATCH (those are immutable) but the SMS UI sends them anyway
 * so the same payload shape works for both verbs.
 */
export interface RubricCellWritePayload {
  role: number;
  urgency?: RubricUrgency | null;
  mode?: RubricMode | null;
  alert_type?: number | null;
  alert_min_level: number | null;
  drill_min_level: number | null;
}

export interface RubricCellListFilters {
  /** Optional role filter — passed straight through to the API. */
  role?: number;
  /** Optional AlertType filter — for type-override cells. */
  alert_type?: number;
  urgency?: RubricUrgency;
  mode?: RubricMode;
  /** Include superseded rows alongside active. Default false on server. */
  include_history?: boolean;
}

function buildQueryString(filters: RubricCellListFilters): string {
  const search = new URLSearchParams();
  if (filters.role != null) search.set('role', String(filters.role));
  if (filters.alert_type != null)
    search.set('alert_type', String(filters.alert_type));
  if (filters.urgency != null) search.set('urgency', filters.urgency);
  if (filters.mode != null) search.set('mode', filters.mode);
  if (filters.include_history) search.set('include_history', 'true');
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

type Tier = 'district' | 'school';

function basePath(tier: Tier, scopeId: number): string {
  return `api/v2/rubric/${tier}/${scopeId}/cells/`;
}

async function jsonOrThrow<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

// =============================================================================
// Preview
// =============================================================================

/**
 * Cell-key shape for a preview request. Exactly one of `(urgency, mode)`
 * or `alert_type` must be set — the server enforces this with a 400
 * response if both or neither are present.
 *
 * Mirrors the CHECK constraint on `DistrictAlertRubricCell` /
 * `SchoolAlertRubricCell`: default cells set `(urgency, mode)`,
 * type-override cells set `alert_type`.
 */
export interface RubricPreviewKey {
  role: number;
  urgency?: RubricUrgency;
  mode?: RubricMode;
  alert_type?: number;
}

/**
 * Upper-bound count returned by `GET /v2/rubric/{tier}/{id}/preview/`.
 * See `preview.py` in the backend for why the count is an upper bound
 * and not the exact materialization fan-out.
 */
export interface RubricPreviewResult {
  affected_users: number;
  affected_schools: number;
  tier: 'district' | 'school';
  scope_id: number;
  cell_key: {
    role: number;
    urgency: RubricUrgency | null;
    mode: RubricMode | null;
    alert_type: number | null;
  };
}

function previewQueryString(key: RubricPreviewKey): string {
  const search = new URLSearchParams();
  search.set('role', String(key.role));
  if (key.alert_type != null) {
    search.set('alert_type', String(key.alert_type));
  } else {
    if (key.urgency) search.set('urgency', key.urgency);
    if (key.mode) search.set('mode', key.mode);
  }
  return `?${search.toString()}`;
}

export async function previewDistrictRubricImpact(
  districtId: number,
  key: RubricPreviewKey,
): Promise<RubricPreviewResult> {
  const response = await client.ky.get(
    `api/v2/rubric/district/${districtId}/preview/${previewQueryString(key)}`,
  );
  return jsonOrThrow<RubricPreviewResult>(response);
}

export async function previewSchoolRubricImpact(
  schoolId: number,
  key: RubricPreviewKey,
): Promise<RubricPreviewResult> {
  const response = await client.ky.get(
    `api/v2/rubric/school/${schoolId}/preview/${previewQueryString(key)}`,
  );
  return jsonOrThrow<RubricPreviewResult>(response);
}

// =============================================================================
// District tier
// =============================================================================

export async function getDistrictRubricCells(
  districtId: number,
  filters: RubricCellListFilters = {},
): Promise<RubricCell[]> {
  const response = await client.ky.get(
    `${basePath('district', districtId)}${buildQueryString(filters)}`,
  );
  return jsonOrThrow<RubricCell[]>(response);
}

export async function createDistrictRubricCell(
  districtId: number,
  body: RubricCellWritePayload,
): Promise<RubricCell> {
  const response = await client.ky.post(basePath('district', districtId), {
    json: body,
  });
  return jsonOrThrow<RubricCell>(response);
}

export async function patchDistrictRubricCell(
  districtId: number,
  cellId: number,
  body: Partial<RubricCellWritePayload>,
): Promise<RubricCell> {
  const response = await client.ky.patch(
    `${basePath('district', districtId)}${cellId}/`,
    { json: body },
  );
  return jsonOrThrow<RubricCell>(response);
}

export async function deleteDistrictRubricCell(
  districtId: number,
  cellId: number,
): Promise<RubricCell> {
  // DELETE returns the new NULL-level row (append-only history); see
  // module docstring.
  const response = await client.ky.delete(
    `${basePath('district', districtId)}${cellId}/`,
  );
  return jsonOrThrow<RubricCell>(response);
}

// =============================================================================
// School tier
// =============================================================================

export async function getSchoolRubricCells(
  schoolId: number,
  filters: RubricCellListFilters = {},
): Promise<RubricCell[]> {
  const response = await client.ky.get(
    `${basePath('school', schoolId)}${buildQueryString(filters)}`,
  );
  return jsonOrThrow<RubricCell[]>(response);
}

export async function createSchoolRubricCell(
  schoolId: number,
  body: RubricCellWritePayload,
): Promise<RubricCell> {
  const response = await client.ky.post(basePath('school', schoolId), {
    json: body,
  });
  return jsonOrThrow<RubricCell>(response);
}

export async function patchSchoolRubricCell(
  schoolId: number,
  cellId: number,
  body: Partial<RubricCellWritePayload>,
): Promise<RubricCell> {
  const response = await client.ky.patch(
    `${basePath('school', schoolId)}${cellId}/`,
    { json: body },
  );
  return jsonOrThrow<RubricCell>(response);
}

export async function deleteSchoolRubricCell(
  schoolId: number,
  cellId: number,
): Promise<RubricCell> {
  const response = await client.ky.delete(
    `${basePath('school', schoolId)}${cellId}/`,
  );
  return jsonOrThrow<RubricCell>(response);
}
